import { IconName } from "@/lib/modules";
import { Input } from "../input";
import { Skeleton } from "../skeleton";
import { FormField } from "./FormField";

// ========== INPUT FIELD ==========
type InputFieldProps = {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  required?: boolean;
  helperText?: string;
  error?: string;
  className?: string;
  icon?: IconName;
  tooltip?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  isLoading?: boolean;
  ref?: React.Ref<HTMLInputElement>;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Mobile keyboard type hint: numeric, tel, email, url, search, decimal, none */
  inputMode?:
    | "none"
    | "text"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal"
    | "search";
};

export function InputField({
  label,
  name,
  type = "text",
  placeholder,
  defaultValue,
  value,
  required = false,
  helperText,
  error,
  className,
  icon,
  tooltip,
  onChange,
  readOnly = false,
  disabled = false,
  hidden = false,
  isLoading = false,
  ref,
  onKeyDown,
  onBlur,
  inputMode,
}: InputFieldProps) {
  // Auto-detect inputMode based on type if not explicitly provided
  const resolvedInputMode =
    inputMode ??
    (type === "email"
      ? "email"
      : type === "tel"
        ? "tel"
        : type === "url"
          ? "url"
          : type === "number"
            ? "decimal"
            : undefined);
  return (
    <FormField
      label={label}
      htmlFor={name}
      required={required}
      helperText={helperText}
      error={error}
      icon={icon}
      tooltip={tooltip}
      hidden={hidden}
    >
      {!isLoading ? (
        <Input
          ref={ref}
          id={name}
          name={name}
          type={type}
          inputMode={resolvedInputMode}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          placeholder={placeholder}
          {...(value !== undefined ? { value } : { defaultValue })}
          required={required}
          className={`${className} ${isLoading && " animate-pulse"} z-10`}
          onChange={onChange}
          readOnly={readOnly}
          disabled={disabled}
          hidden={hidden}
        />
      ) : (
        <div className=" border-[0.5] border-gray-300 dark:border-gray-700 bg-background rounded-lg dark:bg-input/30">
          <Skeleton className="w-full h-8 " />
        </div>
      )}
    </FormField>
  );
}
